import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function AppetiteSettings() {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [defaults, setDefaults] = useState({ tier_a_threshold: 50, tier_b_threshold: 30, require_exception_below: null });
  const [domains, setDomains] = useState([]);
  const [overrides, setOverrides] = useState({}); // {domain_id: {tier_a_threshold, tier_b_threshold, require_exception_below}}

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const d = await fetch(`${API_BASE}/risk-assessment/domains`, { credentials: 'include' }).then(r => r.json());
        if (d.success) setDomains(d.domains || []);
        const a = await fetch(`${API_BASE}/risk-assessment/appetite`, { credentials: 'include' }).then(r => r.json());
        if (a.success && a.appetite) setDefaults(a.appetite);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const saveDefault = async () => {
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/appetite`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...defaults, domain_id: null })
      });
      const json = await res.json();
      if (json.success) setMessage('✅ Saved default appetite'); else setMessage('❌ Save failed');
      setTimeout(() => setMessage(''), 2500);
    } catch { setMessage('❌ Save failed'); setTimeout(() => setMessage(''), 2500); }
  };

  const saveOverride = async (domain_id) => {
    const data = overrides[domain_id];
    if (!data) return;
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/appetite`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, domain_id })
      });
      const json = await res.json();
      if (json.success) setMessage('✅ Saved'); else setMessage('❌ Save failed');
      setTimeout(() => setMessage(''), 2500);
    } catch { setMessage('❌ Save failed'); setTimeout(() => setMessage(''), 2500); }
  };

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="max-w-5xl p-6 space-y-6">
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Default Risk Appetite (organization)</h2>
        <p className="text-sm text-slate-600 mb-4">Used unless you override a specific domain.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-sm">Tier A High threshold
            <input type="number" min="1" max="200" value={defaults.tier_a_threshold}
              onChange={(e)=>setDefaults({...defaults, tier_a_threshold: parseInt(e.target.value)||50})}
              className="w-full mt-1 px-3 py-2 border rounded-lg" />
            <div className="text-xs text-gray-500 mt-1">≥50 points - CAP required</div>
          </label>
          <label className="text-sm">Tier B Medium threshold
            <input type="number" min="1" max="200" value={defaults.tier_b_threshold}
              onChange={(e)=>setDefaults({...defaults, tier_b_threshold: parseInt(e.target.value)||30})}
              className="w-full mt-1 px-3 py-2 border rounded-lg" />
            <div className="text-xs text-gray-500 mt-1">30-49 points - Monitoring plan</div>
          </label>
          <label className="text-sm">Exception Threshold (optional)
            <input type="number" min="0" max="200" value={defaults.require_exception_below||''}
              onChange={(e)=>setDefaults({...defaults, require_exception_below: e.target.value?parseInt(e.target.value):null})}
              className="w-full mt-1 px-3 py-2 border rounded-lg" />
            <div className="text-xs text-gray-500 mt-1">Forces exception process for risks below this threshold</div>
            <div className="text-xs text-blue-600 mt-1">Note: Tier C (&lt;30 points) is automatic - this is separate</div>
          </label>
        </div>
        <div className="mt-4 text-right">
          <button onClick={saveDefault} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Save Default</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Domain Overrides</h2>
        <div className="space-y-4">
          {domains.map(d => (
            <div key={d.id} className="border border-slate-200 rounded-xl p-4">
              <div className="font-medium text-slate-900 mb-2">{d.name}</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="text-sm">Tier A High threshold
                  <input type="number" min="1" max="200" value={overrides[d.id]?.tier_a_threshold ?? ''}
                    onChange={(e)=>setOverrides(o=>({...o,[d.id]:{...o[d.id], tier_a_threshold: parseInt(e.target.value)||undefined}}))}
                    className="w-full mt-1 px-3 py-2 border rounded-lg" />
                  <div className="text-xs text-gray-500 mt-1">≥50 points</div>
                </label>
                <label className="text-sm">Tier B Medium threshold
                  <input type="number" min="1" max="200" value={overrides[d.id]?.tier_b_threshold ?? ''}
                    onChange={(e)=>setOverrides(o=>({...o,[d.id]:{...o[d.id], tier_b_threshold: parseInt(e.target.value)||undefined}}))}
                    className="w-full mt-1 px-3 py-2 border rounded-lg" />
                  <div className="text-xs text-gray-500 mt-1">30-49 points</div>
                </label>
                <label className="text-sm">Exception Threshold
                  <input type="number" min="0" max="200" value={overrides[d.id]?.require_exception_below ?? ''}
                    onChange={(e)=>setOverrides(o=>({...o,[d.id]:{...o[d.id], require_exception_below: e.target.value?parseInt(e.target.value):undefined}}))}
                    className="w-full mt-1 px-3 py-2 border rounded-lg" />
                  <div className="text-xs text-gray-500 mt-1">Forces exception process</div>
                  <div className="text-xs text-blue-600 mt-1">Separate from Tier C</div>
                </label>
              </div>
              <div className="mt-3 text-right">
                <button onClick={()=>saveOverride(d.id)} className="px-3 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg">Save Override</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {message && <div className="text-sm text-slate-700">{message}</div>}
    </div>
  );
}


